import { memo, useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem, Input } from './WaterfallFlows.styled';
import WaterfallAnalysesList from 'components/Analyses/WaterfallAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useWaterfallFlows from 'hooks/useWaterfallFlows';
import useWaterfallAnalyses from 'hooks/useWaterfallAnalyses';
import useBlob from 'hooks/useBlob';
import useChecksum from 'hooks/useChecksum';
import { DELETE_WATERFALL_FLOW } from 'helpers/confirmations';
import { checkEmptyString, checkValidEmail, needToUpdateField } from 'helpers';
import { waterfallFlowsSelectors } from "features/waterfallFlows";
import { blobsSelectors } from 'features/blobs';

const WaterfallFlows = () => {
  const inputRef = useRef(null);
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getWaterfallFlow, createWaterfallFlow, updateWaterfallFlow } = useWaterfallFlows();
  const { getWaterfallAnalyses, createWaterfallAnalysis, clearLocalWaterfallAnalyses } = useWaterfallAnalyses();
  const { processBlob } = useBlob();
  const { processFiles } = useChecksum();
  const id = useCustomSelector(waterfallFlowsSelectors.getWaterfallFlowId);
  const waterfallFlow = useCustomSelector(waterfallFlowsSelectors.getWaterfallFlowData);
  const fetching = useCustomSelector(waterfallFlowsSelectors.waterfallFlowIsFetching);
  const blobFetching = useCustomSelector(blobsSelectors.blobIsFetching);
  const [email, setEmail] = useState({ value: '', error: '' });
  const [firstPrompt, setFirstPrompt] = useState({ value: '', error: '' });
  const [secondPrompt, setSecondPrompt] = useState({ value: '', error: '' });
  const [thirdPrompt, setThirdPrompt] = useState({ value: '', error: '' });
  const [fourthPrompt, setFourthPrompt] = useState({ value: '', error: '' });
  const [file, setFile] = useState({ value: null, formattedValue: null });
  const [edit, setEdit] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (waterfall_flow) => {
    setEmail({ value: waterfall_flow?.email, error: '' });
    setFirstPrompt({ value: waterfall_flow?.first_prompt, error: '' });
    setSecondPrompt({ value: waterfall_flow?.second_prompt, error: '' });
    setThirdPrompt({ value: waterfall_flow?.third_prompt, error: '' });
    setFourthPrompt({ value: waterfall_flow?.fourth_prompt, error: '' });
  };
  const handleExited = () => {
    setEmail({ value: '', error: '' });
    setFirstPrompt({ value: '', error: '' });
    setSecondPrompt({ value: '', error: '' });
    setThirdPrompt({ value: '', error: '' });
    setFourthPrompt({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkValidEmail(email.value)) {
      setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
      hasError = true;
    }
    if (!checkEmptyString(firstPrompt.value)) {
      setFirstPrompt(prev => ({ ...prev, error: `First Prompt can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(secondPrompt.value)) {
      setSecondPrompt(prev => ({ ...prev, error: `Second Prompt can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(thirdPrompt.value)) {
      setThirdPrompt(prev => ({ ...prev, error: `Third Prompt can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(fourthPrompt.value)) {
      setFourthPrompt(prev => ({ ...prev, error: `Fourth Prompt can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const waterfall_flow = {
      ...(needToUpdateField(waterfallFlow?.email, email.value) && {  email: email.value }),
      ...(needToUpdateField(waterfallFlow?.first_prompt, firstPrompt.value) && { first_prompt: firstPrompt.value }),
      ...(needToUpdateField(waterfallFlow?.second_prompt, secondPrompt.value) && { second_prompt: secondPrompt.value }),
      ...(needToUpdateField(waterfallFlow?.third_prompt, thirdPrompt.value) && { third_prompt: thirdPrompt.value }),
      ...(needToUpdateField(waterfallFlow?.fourth_prompt, fourthPrompt.value) && { fourth_prompt: fourthPrompt.value }),
    };

    if (!_isEmpty(waterfall_flow)) {
      if (!!id) {
        updateWaterfallFlow(waterfall_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createWaterfallFlow(waterfall_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_WATERFALL_FLOW });
  const handleUploadClick = () => inputRef.current.click();
  const handleFileChange = ({ target: { files } }) => {
    processFiles(files, ({ file, checksum }) =>
      processBlob({ file, checksum }, blob => { 
        setFile({ value: blob.id, formattedValue: file });
      })
    );
  };
  const handleCreateSeconAnalysis = () => {
    const waterfall_analysis = { 
      input: file.value
    };
    createWaterfallAnalysis(waterfall_analysis)
    .then(({ error }) => {
      if (!error) {
        setFile({ value: null, formattedValue: null });
      }
    })
  };
  const fetchWaterfallAnalyses = useCallback(() => {
    getWaterfallAnalyses({ offset: 0 });
  }, [getWaterfallAnalyses]);
  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
  }

  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalWaterfallAnalyses();
    };
  }, [id, clearLocalWaterfallAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getWaterfallFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { waterfall_flow } = payload;
          handleEntering(waterfall_flow);
          fetchWaterfallAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getWaterfallFlow, fetchWaterfallAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label='Email'
              disabled={fetching || (!!id && !edit)}
              value={email.value}
              onChange={({ target: { value } }) => setEmail({ value, error: '' })}
              helperText={email.error}
              error={!!email.error}
            />
            <Accordion
              expanded={expanded} 
              onChange={handleChangeExpand}
              sx={{ width: '100%' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>Prompts</AccordionSummary>
              <AccordionDetails>
                <TextField
                  fullWidth
                  label='First Prompt'
                  multiline
                  margin='dense'
                  minRows={3}
                  maxRows={10}
                  disabled={fetching || (!!id && !edit)}
                  helperText={firstPrompt.error}
                  error={!!firstPrompt.error}
                  value={firstPrompt.value}
                  onChange={({ target: { value } }) => setFirstPrompt({ value, error: '' })}
                />
                <TextField
                  fullWidth
                  label='Second Prompt'
                  multiline
                  margin='dense'
                  minRows={3}
                  maxRows={10}
                  disabled={fetching || (!!id && !edit)}
                  helperText={secondPrompt.error}
                  error={!!secondPrompt.error}
                  value={secondPrompt.value}
                  onChange={({ target: { value } }) => setSecondPrompt({ value, error: '' })}
                />
                <TextField
                  fullWidth
                  label='Third Prompt'
                  multiline
                  margin='dense'
                  minRows={3}
                  maxRows={10}
                  disabled={fetching || (!!id && !edit)}
                  helperText={thirdPrompt.error}
                  error={!!thirdPrompt.error}
                  value={thirdPrompt.value}
                  onChange={({ target: { value } }) => setThirdPrompt({ value, error: '' })}
                />
                <TextField
                  fullWidth
                  label='Fourth Prompt'
                  multiline
                  margin='dense'
                  minRows={3}
                  maxRows={10}
                  disabled={fetching || (!!id && !edit)}
                  helperText={fourthPrompt.error}
                  error={!!fourthPrompt.error}
                  value={fourthPrompt.value}
                  onChange={({ target: { value } }) => setFourthPrompt({ value, error: '' })}
                />
              </AccordionDetails>
            </Accordion>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Upload</Typography>
            {!!file.value && <Button variant='outlined' disabled={fetching || blobFetching} sx={{ mr: '5px' }} onClick={handleCreateSeconAnalysis}>Send</Button>}
          </CardItem>
          <Box display='flex' justifyContent='center' alignItems='center'>
            <Button 
              color='primary' 
              startIcon={<AddIcon />} 
              endIcon={blobFetching && <CircularProgress size={20} color='inherit' />}
              disabled={ blobFetching || !id} 
              onClick={handleUploadClick}
            >
              PDF FILE
            </Button>
            {!!file.value && <Typography variant='body2' noWrap>{file?.formattedValue?.name}</Typography>}
            <Input
                ref={inputRef}
                accept=".pdf"
                id="documents"
                type="file"
                onClick={(event) => event.target.value = ''}
                onChange={handleFileChange}
            />
          </Box>
        </Card>  
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <WaterfallAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <WaterfallFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);